import axios from 'axios'
import * as jwt from 'jsonwebtoken'
import { Tenant } from '@/types'

/**
  POC — passkey LOGIN via the Auth0 Native Passkeys API (no Universal Login).

  Flow:
    1. POST /passkey/challenge            -> WebAuthn request options + auth_session
    2. navigator.credentials.get()        -> the authenticator signs the challenge (browser-only)
    3. POST /oauth/token (webauthn grant) -> user tokens

  The whole point of this POC: unlike ACUL login (which omits rpId and falls back to the
  page origin), here WE build the get() options — so we can set `rpId` explicitly and make
  it match what the passkey was enrolled under (e.g. the parent `dev.siteminderlabs.com`).

  NOTE (EA): confirm the exact request/response field names + the webauthn grant params
  against the Native Passkeys API reference before hardening. Everything is logged for now.
* */

const WEBAUTHN_GRANT = 'urn:okta:params:oauth:grant-type:webauthn'

/**
  STEP 0 PROBE — throwaway diagnostics, delete before anything ships.

  Two questions decide whether the session-transfer path is viable at all:

    U2  does the webauthn grant return a refresh_token when we ask for offline_access?
        (no refresh token -> nothing to exchange for a session_transfer_token -> path dead)

    U3  does the id_token carry `sid` and the `https://siteminder.com/*` custom claims
        set by the enrich-profile-with-geoip post-login action? (tech design P5 / R1)
        Without them, auth-api cannot build a Session and the whole authx session model
        degrades regardless of which login approach we pick.

  `scope` and `audience` belong on the /oauth/token request, not /passkey/challenge —
  confirmed against auth0-spa-js `_requestTokenForPasskey`.
* */
const probeTokenResponse = (tokens: any) => {

  const idToken = tokens?.id_token ? jwt.decode(tokens.id_token) as any : null

  const claim = (name: string) => (idToken?.[`https://siteminder.com/${name}`] ? 'present' : 'MISSING')

  // eslint-disable-next-line no-console
  console.log('[passkey-login-poc] STEP 0 probe', {
    // U2
    refresh_token: tokens?.refresh_token ? `present (len ${tokens.refresh_token.length})` : 'MISSING',
    granted_scope: tokens?.scope ?? '(none returned)',
    token_type: tokens?.token_type,
    expires_in: tokens?.expires_in,
    // U3 / P5
    id_token: tokens?.id_token ? 'present' : 'MISSING',
    sid: idToken?.sid ?? 'MISSING',
    claimSession: claim('session'),
    claimAuthenticationMethods: claim('authenticationMethods'),
    claimRiskAssessment: claim('riskAssessment'),
    claimGeoip: claim('geoip'),
  })

  // eslint-disable-next-line no-console
  console.log('[passkey-login-poc] decoded id_token', idToken)

  // Parked on window so step 2 (refresh_token -> session_transfer_token exchange) can be
  // driven straight from the console without copying a credential out of the log.
  // Dev-only convenience; never do this anywhere real.
  ;(window as any).__passkeyPocTokens = tokens

}

// base64url <-> ArrayBuffer (WebAuthn transports values as base64url)
const base64UrlToBuffer = (value: string): ArrayBuffer => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

const bufferToBase64Url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

// Shape the server challenge into PublicKeyCredentialRequestOptions, forcing our rpId.
const toRequestOptions = (publicKey: any, rpId: string): any => ({
  ...publicKey,
  rpId,
  challenge: base64UrlToBuffer(publicKey.challenge),
  allowCredentials: (publicKey.allowCredentials || []).map((cred: any) => ({
    ...cred,
    id: base64UrlToBuffer(cred.id),
  })),
})

// Shape the browser's assertion into the JSON the token endpoint expects.
const toAuthnResponse = (credential: any) => {
  const { response } = credential
  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      authenticatorData: bufferToBase64Url(response.authenticatorData),
      signature: bufferToBase64Url(response.signature),
      userHandle: response.userHandle ? bufferToBase64Url(response.userHandle) : null,
    },
  }
}

/**
 * @param rpIdOverride - the rpId to request. Must EXACTLY match the rpId the passkey was
 *   enrolled under (e.g. `dev.siteminderlabs.com`). This is the lever we're proving.
 */
export const loginWithPasskey = async (tenant: Tenant, rpIdOverride?: string): Promise<any> => {

  const origin = `https://${tenant.loginDomain}`

  // 1. Challenge (unauthenticated — no bearer token).
  const { data: challenge } = await axios.post(`${origin}/passkey/challenge`, {
    client_id: tenant.loginClientId,
    // realm: '<connection name>'  // add if the tenant requires it
  })

  // eslint-disable-next-line no-console
  console.log('[passkey-login-poc] challenge', challenge)

  // 2. Decide the rpId we'll request, then run the WebAuthn ceremony.
  const rpId = rpIdOverride
    || challenge.authn_params_public_key?.rpId
    || tenant.loginDomain

  // eslint-disable-next-line no-console
  console.log('[passkey-login-poc] requesting rpId =', rpId)

  const credential = await navigator.credentials.get({
    publicKey: toRequestOptions(challenge.authn_params_public_key, rpId),
  }) as any

  if (!credential) {
    throw new Error('PasskeyLoginCancelled')
  }

  // 3. Exchange the assertion for tokens via the webauthn grant.
  const { data: tokens } = await axios.post(`${origin}/oauth/token`, {
    grant_type: WEBAUTHN_GRANT,
    client_id: tenant.loginClientId,
    auth_session: challenge.auth_session,
    authn_response: toAuthnResponse(credential),
    // STEP 0: offline_access asks for the refresh_token that session transfer needs as its
    // subject_token.
    //
    // NO `audience` deliberately. Auth0 only issues a refresh token when the requested
    // audience's API has "Allow Offline Access" enabled, and `Auth0 My Account API` does
    // not (the resource server has no allow_offline_access, and it's is_system so we
    // can't set it) — asking for that audience silently drops offline_access and you get
    // no refresh token. Confirmed empirically.
    //
    // We don't need the /me/ audience here anyway: if session transfer works we get a real
    // Auth0 cookie, and the cookie makes checkSession -> getUserToken work exactly as it
    // already does for enrolment.
    scope: 'openid profile offline_access',
  })

  // eslint-disable-next-line no-console
  console.log('[passkey-login-poc] tokens', tokens)

  probeTokenResponse(tokens)

  return tokens
}
